import React, { useEffect, useState } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { CheckCircle2, Clock } from 'lucide-react';
import { entriesApi, ApprovalEntry } from '../../../api/entries';
import { formatDate, getDayName, cn } from '../../../lib/utils';
import { useDebouncedValue } from '../../../lib/useDebouncedValue';
import { Button } from '../../../components/ui/atoms/button';
import { StatusPill } from '../../../components/ui/atoms/status-pill';
import { Input } from '../../../components/ui/atoms/input';
import { Table, TableHeader, TableBody, TableRow, TableHead, TableCell } from '../../../components/ui/molecules/table';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '../../../components/ui/organisms/dialog';
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from '../../../components/ui/molecules/select';
import { SearchableSelect } from '../../../components/ui/molecules/searchable-select';
import { EntryActionsMenu } from '../../../components/ui/organisms/entry-actions-menu';
import { NoteDialog } from '../../../components/ui/organisms/note-dialog';
import { STATUS_CONFIG, WIDEST_STATUS_LABEL } from '../../../components/ui/organisms/calendar-view';
import { TimeEntryStatusEnum } from '../../../types';
import { NoteDialogState } from './interface';
import { QUEUE_STATUSES, invalidateApprovalsQueries } from './utils';
import { RoleBadge } from './components';

export function ApprovalsPage() {
  const queryClient = useQueryClient();
  const [page, setPage] = useState(1);
  const [status, setStatus] = useState('');
  const [name, setName] = useState('');
  const debouncedName = useDebouncedValue(name.trim(), 300);
  const [projectId, setProjectId] = useState('');
  const [selectedEntryIds, setSelectedEntryIds] = useState<Set<string>>(new Set());
  const [isApproveConfirmOpen, setIsApproveConfirmOpen] = useState(false);

  // Dialog state for Request Edit / Reject (both need an optional note)
  const [noteDialog, setNoteDialog] = useState<NoteDialogState | null>(null);

  useEffect(() => {
    setPage(1);
  }, [status, debouncedName, projectId]);

  const { data, isLoading } = useQuery({
    queryKey: ['approvals-queue', page, status, debouncedName, projectId],
    queryFn: () =>
      entriesApi
        .approvalsQueue({ page, limit: 20, status: status || undefined, name: debouncedName || undefined, projectId: projectId || undefined })
        .then((r) => r.data),
  });

  const { data: projectsData } = useQuery({
    queryKey: ['approvals-queue-projects'],
    queryFn: () => entriesApi.approvableProjects().then((r) => r.data),
  });
  const projects = projectsData ?? [];

  const entries: ApprovalEntry[] = data?.data ?? [];
  const meta = data?.meta;

  const approveMutation = useMutation({
    mutationFn: (entryIds: string[]) => entriesApi.approveBatch(entryIds),
    onSuccess: () => {
      setSelectedEntryIds(new Set());
      setIsApproveConfirmOpen(false);
      invalidateApprovalsQueries(queryClient);
    },
  });

  const allEntriesSelected = entries.length > 0 && entries.every((entry) => selectedEntryIds.has(entry.id));
  const toggleEntry = (id: string) => {
    setSelectedEntryIds((current) => {
      const next = new Set(current);
      if (next.has(id)) next.delete(id);
      else next.add(id);
      return next;
    });
  };
  const toggleAllEntries = () => {
    setSelectedEntryIds((current) => {
      const next = new Set(current);
      if (allEntriesSelected) entries.forEach((entry) => next.delete(entry.id));
      else entries.forEach((entry) => next.add(entry.id));
      return next;
    });
  };
  const openApproveConfirm = (entryIds?: string[]) => {
    if (entryIds) setSelectedEntryIds(new Set(entryIds));
    setIsApproveConfirmOpen(true);
  };

  // Group by date for readability, similar to the entries list
  const grouped: Record<string, ApprovalEntry[]> = {};
  for (const entry of entries) {
    const dateStr = entry.date.slice(0, 10);
    (grouped[dateStr] ??= []).push(entry);
  }
  const dates = Object.keys(grouped).sort((a, b) => a.localeCompare(b));

  return (
    <div className="space-y-6">
      <div>
        <h1 className="text-2xl font-bold text-slate-900 dark:text-white">Approvals</h1>
        <p className="text-sm text-muted-foreground">
          Time entries awaiting your review as Scrum Master, Project Lead, or Direct Manager.
        </p>
      </div>

      <div className="flex flex-wrap items-end gap-3">
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Employee</label>
          <Input
            placeholder="Search by name…"
            value={name}
            onChange={(e) => setName(e.target.value)}
            className="w-56"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Project</label>
          <SearchableSelect
            value={projectId}
            onValueChange={setProjectId}
            options={projects.map((p) => ({ value: p.id, label: p.name }))}
            allowEmpty
            emptyLabel="All projects"
            placeholder="All projects"
            searchPlaceholder="Search projects..."
            className="w-48"
          />
        </div>
        <div className="flex flex-col gap-1">
          <label className="text-xs text-muted-foreground">Status</label>
          <Select value={status} onValueChange={(v) => setStatus(v === '__all__' ? '' : v)}>
            <SelectTrigger className="w-48">
              <SelectValue placeholder="All statuses" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__all__">All statuses</SelectItem>
              {QUEUE_STATUSES.map((s) => (
                <SelectItem key={s} value={s}>{STATUS_CONFIG[s].label}</SelectItem>
              ))}
            </SelectContent>
          </Select>
        </div>
        {(status || name || projectId) && (
          <Button variant="ghost" size="sm" onClick={() => { setStatus(''); setName(''); setProjectId(''); }}>Clear</Button>
        )}
        <Button disabled={selectedEntryIds.size === 0} onClick={() => openApproveConfirm()}>
          <CheckCircle2 className="mr-2 h-4 w-4" />
          Approve selected ({selectedEntryIds.size})
        </Button>
      </div>

      {isLoading ? (
        <div className="flex h-40 items-center justify-center">
          <div className="h-8 w-8 animate-spin rounded-full border-4 border-primary border-t-transparent" />
        </div>
      ) : entries.length === 0 ? (
        <div className="flex flex-col items-center justify-center gap-2 rounded-lg border border-dashed py-16 text-center">
          <CheckCircle2 className="h-8 w-8 text-muted-foreground" />
          <p className="text-sm text-muted-foreground">Nothing waiting on your approval right now.</p>
        </div>
      ) : (
        <>
          <Table>
            <TableHeader>
              <TableRow>
                <TableHead className="w-10 px-3">
                  <input
                    type="checkbox"
                    aria-label="Select all entries on this page"
                    checked={allEntriesSelected}
                    onChange={toggleAllEntries}
                    className="h-4 w-4 accent-primary"
                  />
                </TableHead>
                <TableHead>Employee</TableHead>
                <TableHead className="w-48">Project</TableHead>
                <TableHead>Phase</TableHead>
                <TableHead className="w-36">Job Title</TableHead>
                <TableHead>Hours</TableHead>
                <TableHead>Status</TableHead>
                <TableHead>Notes</TableHead>
                <TableHead>Role</TableHead>
                <TableHead className="text-right">Actions</TableHead>
              </TableRow>
            </TableHeader>
            <TableBody>
              {dates.map((dateStr) => {
                const dateEntries = grouped[dateStr];
                const allDateEntriesSelected = dateEntries.every((entry) => selectedEntryIds.has(entry.id));
                const toggleDateEntries = () => {
                  setSelectedEntryIds((current) => {
                    const next = new Set(current);
                    if (allDateEntriesSelected) dateEntries.forEach((entry) => next.delete(entry.id));
                    else dateEntries.forEach((entry) => next.add(entry.id));
                    return next;
                  });
                };

                return (
                <React.Fragment key={dateStr}>
                  <TableRow className="bg-muted/40 hover:bg-muted/40">
                    <TableCell className="w-10 px-3 py-1.5">
                      <input
                        type="checkbox"
                        aria-label={`Select all entries on ${getDayName(dateStr)}`}
                        checked={allDateEntriesSelected}
                        onChange={toggleDateEntries}
                        className="h-4 w-4 accent-primary"
                      />
                    </TableCell>
                    <TableCell colSpan={9} className="py-1.5">
                      <span className="text-sm font-medium">{getDayName(dateStr)}</span>
                      <span className="ml-2 text-xs text-muted-foreground">{formatDate(dateStr + 'T00:00:00')}</span>
                    </TableCell>
                  </TableRow>
                  {dateEntries.map((entry) => {
                    const entryStatus = STATUS_CONFIG[entry.status];
                    return (
                    <TableRow
                      key={entry.id}
                      className={cn(
                        entry.isDifferentJobTitle && 'bg-orange-50 hover:bg-orange-100 dark:bg-orange-950/30 dark:hover:bg-orange-950/40 ring-1 ring-inset ring-orange-300 dark:ring-orange-800',
                      )}
                      title={entry.isDifferentJobTitle ? "Job title differs from this employee's default" : undefined}
                    >
                      <TableCell className="px-3">
                        <input
                          type="checkbox"
                          aria-label={`Select ${entry.user?.name ?? 'time entry'} on ${dateStr}`}
                          checked={selectedEntryIds.has(entry.id)}
                          onChange={() => toggleEntry(entry.id)}
                          className="h-4 w-4 accent-primary"
                        />
                      </TableCell>
                      <TableCell className="font-medium">{entry.user?.name ?? '—'}</TableCell>
                      <TableCell>{entry.project?.name ?? '—'}</TableCell>
                      <TableCell className="text-muted-foreground">{entry.phase?.name ?? '—'}</TableCell>
                      <TableCell className={entry.isDifferentJobTitle ? "" : "text-muted-foreground"}>{entry.jobTitle?.name ?? '—'}</TableCell>
                      <TableCell>
                        <div className="flex items-center gap-1 text-sm">
                          <Clock className="h-3.5 w-3.5 text-muted-foreground" />
                          {entry.hours}h
                        </div>
                      </TableCell>
                      <TableCell>
                        <StatusPill {...entryStatus} sizeTo={WIDEST_STATUS_LABEL} />
                      </TableCell>
                      <TableCell className="w-40 max-w-[10rem] text-muted-foreground whitespace-normal break-words text-xs">{entry.notes ?? '—'}</TableCell>
                      <TableCell><RoleBadge role={entry.callerRole} /></TableCell>
                      <TableCell className="text-right">
                        <EntryActionsMenu
                          onApprove={() => openApproveConfirm([entry.id])}
                          approveDisabled={approveMutation.isPending}
                          onRequestEdit={() => setNoteDialog({ entry, action: 'request-edit' })}
                          onReject={() => setNoteDialog({ entry, action: 'reject' })}
                          // Reject is only allowed at PENDING_SCRUM or PENDING_MANAGER per backend rules
                          canReject={entry.status === TimeEntryStatusEnum.PENDING_SCRUM || entry.status === TimeEntryStatusEnum.PENDING_MANAGER}
                        />
                      </TableCell>
                    </TableRow>
                    );
                  })}
                </React.Fragment>
                );
              })}
            </TableBody>
          </Table>

          {meta && meta.totalPages > 1 && (
            <div className="flex items-center justify-end gap-2">
              <Button variant="outline" size="sm" disabled={page <= 1} onClick={() => setPage((p) => p - 1)}>Previous</Button>
              <span className="text-sm text-muted-foreground">Page {meta.page} of {meta.totalPages}</span>
              <Button variant="outline" size="sm" disabled={page >= meta.totalPages} onClick={() => setPage((p) => p + 1)}>Next</Button>
            </div>
          )}
        </>
      )}

      <Dialog open={!!noteDialog} onOpenChange={(open) => !open && setNoteDialog(null)}>
        {noteDialog && (
          <NoteDialog
            entry={noteDialog.entry}
            action={noteDialog.action}
            onClose={() => setNoteDialog(null)}
            onSuccess={() => invalidateApprovalsQueries(queryClient)}
          />
        )}
      </Dialog>

      <Dialog open={isApproveConfirmOpen} onOpenChange={setIsApproveConfirmOpen}>
        <DialogContent className="max-w-md">
          <DialogHeader>
            <DialogTitle>Confirm approval</DialogTitle>
          </DialogHeader>
          <p className="text-sm text-muted-foreground">
            Are you sure you want to approve {selectedEntryIds.size} time {selectedEntryIds.size === 1 ? 'entry' : 'entries'}? You will be responsible for these approvals.
          </p>
          {approveMutation.error && (
            <p className="text-sm text-destructive">
              {(approveMutation.error as any)?.response?.data?.error || 'Failed to approve the selected entries.'}
            </p>
          )}
          <div className="flex justify-end gap-2">
            <Button variant="outline" onClick={() => setIsApproveConfirmOpen(false)} disabled={approveMutation.isPending}>Cancel</Button>
            <Button onClick={() => approveMutation.mutate(Array.from(selectedEntryIds))} disabled={approveMutation.isPending}>
              {approveMutation.isPending ? 'Approving...' : 'Yes, approve'}
            </Button>
          </div>
        </DialogContent>
      </Dialog>
    </div>
  );
}
